import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Mail, MapPin, MessageCircle, Phone, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { BRAND, BUSINESS_UNITS } from "@/lib/eraContent";
import { useLang } from "@/lib/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const { t } = useLang();
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t.contact.toastMissing);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.toastSuccess);
      setForm({ name: "", email: "", company: "", phone: "", message: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        t.contact.toastError;
      toast.error(typeof msg === "string" ? msg : t.contact.toastError);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-navy-900 text-white overflow-hidden"
    >
      {/* Abstract gradient mesh background */}
      <div className="absolute inset-0 hero-mesh opacity-90" />
      <div className="absolute inset-0 hero-grid opacity-60 pointer-events-none" />
      <div className="absolute -top-24 left-1/3 w-[520px] h-[520px] rounded-full bg-blue-600/20 blur-[140px]" />
      <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-gold/15 blur-[120px]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <span className="text-xs uppercase tracking-[0.28em] font-semibold text-gold">
            {t.contact.eyebrow}
          </span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tighter text-white">
            {t.contact.title}{" "}
            <span className="bg-gradient-to-r from-gold via-gold-400 to-gold-300 bg-clip-text text-transparent">
              {t.contact.titleAccent}
            </span>
          </h2>
          <p className="mt-6 text-slate-300 text-base md:text-lg leading-relaxed max-w-2xl">
            {t.contact.subtitle}
          </p>
        </div>

        <div className="mt-16 grid lg:grid-cols-12 gap-8">
          {/* Business Unit shortcuts + info */}
          <div className="lg:col-span-5 space-y-4">
            <p className="text-xs uppercase tracking-[0.22em] font-semibold text-slate-400 mb-2">
              {t.contact.visitBUTitle}
            </p>
            {BUSINESS_UNITS.map((unit) => {
              const isComingSoon = unit.website === "#";
              const Wrapper = isComingSoon ? "div" : "a";
              const linkProps = isComingSoon
                ? {}
                : {
                    href: unit.website,
                    target: "_blank",
                    rel: "noopener noreferrer",
                  };
              return (
                <Wrapper
                  key={unit.id}
                  {...linkProps}
                  data-testid={`contact-visit-${unit.id}`}
                  className={
                    "group flex items-center justify-between gap-4 rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-5 md:p-6 transition-all " +
                    (isComingSoon
                      ? "cursor-default"
                      : "hover:bg-white/10 hover:border-white/20")
                  }
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="h-14 w-14 rounded-2xl bg-white flex items-center justify-center shrink-0">
                      <img
                        src={unit.logo}
                        alt={unit.name}
                        className="max-h-9 max-w-11 object-contain"
                      />
                    </div>
                    <div className="min-w-0">
                      <div className="font-display font-semibold text-white truncate">
                        {unit.name}
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5 truncate">
                        {isComingSoon
                          ? t.ecosystem.comingSoon
                          : unit.website.replace("https://", "")}
                      </div>
                    </div>
                  </div>
                  <div
                    className={
                      "h-10 w-10 rounded-full flex items-center justify-center shrink-0 transition-transform " +
                      (isComingSoon
                        ? "bg-white/10 text-slate-400"
                        : "bg-gold text-navy-900 group-hover:rotate-45")
                    }
                  >
                    <ArrowUpRight className="w-5 h-5" />
                  </div>
                </Wrapper>
              );
            })}

            <div className="mt-8 rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-6 space-y-4">
              <p className="text-xs uppercase tracking-[0.22em] font-semibold text-slate-400">
                {t.contact.reachTitle}
              </p>
              <div className="flex items-start gap-3 text-sm">
                <Mail className="w-4 h-4 text-gold mt-0.5" />
                <a
                  href={`mailto:${BRAND.email}`}
                  data-testid="contact-info-email"
                  className="text-slate-200 hover:text-white break-all"
                >
                  {BRAND.email}
                </a>
              </div>
              <div className="flex items-start gap-3 text-sm">
                <Phone className="w-4 h-4 text-gold mt-0.5" />
                <a
                  href={`https://wa.me/${BRAND.phoneDigits}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-info-phone"
                  className="text-slate-200 hover:text-white"
                >
                  {BRAND.phone}
                </a>
              </div>
              <div className="flex items-start gap-3 text-sm">
                <MapPin className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                <span
                  data-testid="contact-info-address"
                  className="text-slate-300 leading-relaxed"
                >
                  {BRAND.address}
                </span>
              </div>

              <a
                href={`https://wa.me/${BRAND.phoneDigits}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-cta"
                className="group mt-2 inline-flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-gradient-to-br from-emerald-500/20 via-white/5 to-transparent px-4 py-3 hover:border-white/20 transition-all"
              >
                <span className="flex items-center gap-2.5 text-sm font-medium text-white">
                  <MessageCircle className="w-4 h-4 text-emerald-400" />
                  {t.contact.whatsapp}
                </span>
                <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-white group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
              </a>
            </div>
          </div>

          {/* Form */}
          <motion.form
            data-testid="contact-form"
            onSubmit={onSubmit}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className="lg:col-span-7 rounded-3xl bg-white/[0.04] backdrop-blur-xl border border-white/10 p-6 md:p-10"
          >
            <p className="text-xs uppercase tracking-[0.22em] font-semibold text-slate-400 mb-6">
              {t.contact.formTitle}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-200 text-sm">
                  {t.contact.name} <span className="text-gold">*</span>
                </Label>
                <Input
                  id="name"
                  name="name"
                  data-testid="contact-input-name"
                  value={form.name}
                  onChange={onChange}
                  placeholder={t.contact.namePh}
                  className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 h-12 rounded-xl focus-visible:ring-gold"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-200 text-sm">
                  {t.contact.email} <span className="text-gold">*</span>
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  data-testid="contact-input-email"
                  value={form.email}
                  onChange={onChange}
                  placeholder={t.contact.emailPh}
                  className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 h-12 rounded-xl focus-visible:ring-gold"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company" className="text-slate-200 text-sm">
                  {t.contact.company}
                </Label>
                <Input
                  id="company"
                  name="company"
                  data-testid="contact-input-company"
                  value={form.company}
                  onChange={onChange}
                  placeholder={t.contact.companyPh}
                  className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 h-12 rounded-xl focus-visible:ring-gold"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-slate-200 text-sm">
                  {t.contact.phone}
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  data-testid="contact-input-phone"
                  value={form.phone}
                  onChange={onChange}
                  placeholder={t.contact.phonePh}
                  className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 h-12 rounded-xl focus-visible:ring-gold"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="message" className="text-slate-200 text-sm">
                  {t.contact.message} <span className="text-gold">*</span>
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={onChange}
                  rows={5}
                  placeholder={t.contact.messagePh}
                  className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 rounded-xl focus-visible:ring-gold resize-none"
                  required
                />
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="text-xs text-slate-400 max-w-sm leading-relaxed">
                {t.contact.disclaimer}
              </p>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-btn"
                className="rounded-full h-12 px-7 bg-gold hover:bg-gold-400 text-navy-900 font-semibold min-w-[180px]"
              >
                {submitting ? (
                  t.contact.submitting
                ) : (
                  <>
                    {t.contact.submit}
                    <Send className="ml-2 w-4 h-4" />
                  </>
                )}
              </Button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
